import DOMPurify from "dompurify";
import { marked } from "marked";
import katex from "katex";

/**
 * Pipeline used by every place we render LLM- / textbook-authored content:
 *
 *   `$$...$$` and `\[...\]`      -> KaTeX display mode
 *   `$...$` and `\(...\)`        -> KaTeX inline mode
 *   anything else                -> marked (GFM) -> DOMPurify
 *
 * Math is extracted to opaque text placeholders BEFORE marked runs, so
 * markdown parsing can't mangle the LaTeX (e.g. `$x_1$` no longer looks
 * like emphasis). After DOMPurify sanitises the HTML, the placeholders
 * are substituted with KaTeX's renderToString output -- KaTeX produces
 * its own escaped HTML that's safe to inject. Malformed TeX renders as
 * the source text in red rather than crashing the page.
 *
 * Two delimiter dialects are supported because content sources differ:
 *   - Textbook summaries (manifest .txt) use `$...$` / `$$...$$`
 *   - LLM responses tend to emit `\(...\)` / `\[...\]`
 */
export type RenderOptions = {
  /**
   * Convert single newlines to `<br>` (GFM-style soft breaks). Default
   * `true` -- LLM chat responses rely on it. Pass `false` for prose
   * authored with explicit paragraph spacing (textbook summaries), so
   * a soft break between an intro line and a `$$...$$` block doesn't
   * produce a spurious leading `<br>` inside the list item.
   */
  breaks?: boolean;
};

export function renderMarkdownWithMath(source: string, opts: RenderOptions = {}): string {
  const blocks: string[] = [];
  const inlines: string[] = [];

  let extracted = source;

  // 1a. Block math: $$...$$  (greedy across lines, non-greedy within).
  extracted = extracted.replace(/\$\$([\s\S]+?)\$\$/g, (_, expr: string) => {
    blocks.push(expr.trim());
    return MATH_BLOCK_TOKEN(blocks.length - 1);
  });

  // 1b. Block math: \[ ... \]
  extracted = extracted.replace(/\\\[([\s\S]+?)\\\]/g, (_, expr: string) => {
    blocks.push(expr.trim());
    return MATH_BLOCK_TOKEN(blocks.length - 1);
  });

  // 2a. Inline math: $...$  (single-line, not abutting another $).
  extracted = extracted.replace(/(^|[^$])\$([^\n$]+?)\$(?!\$)/g, (_, pre: string, expr: string) => {
    inlines.push(expr.trim());
    return `${pre}${MATH_INLINE_TOKEN(inlines.length - 1)}`;
  });

  // 2b. Inline math: \( ... \)
  extracted = extracted.replace(/\\\(([\s\S]+?)\\\)/g, (_, expr: string) => {
    inlines.push(expr.trim());
    return MATH_INLINE_TOKEN(inlines.length - 1);
  });

  // 3. Markdown -> HTML.
  const rawHtml = marked.parse(extracted, {
    async: false,
    gfm: true,
    breaks: opts.breaks ?? true,
  }) as string;

  // 4. Sanitise.
  const safe = DOMPurify.sanitize(rawHtml, { USE_PROFILES: { html: true } });

  // 5. Substitute placeholders with KaTeX output.
  return safe
    .replace(MATH_BLOCK_RE,  (_, idx) => renderMath(blocks[Number(idx)]  ?? "", true))
    .replace(MATH_INLINE_RE, (_, idx) => renderMath(inlines[Number(idx)] ?? "", false));
}

// --------------------------------------------------------------------------
const MATH_BLOCK_TOKEN  = (n: number) => `@@STX_MATH_BLOCK_${n}@@`;
const MATH_INLINE_TOKEN = (n: number) => `@@STX_MATH_INLINE_${n}@@`;
const MATH_BLOCK_RE     = /@@STX_MATH_BLOCK_(\d+)@@/g;
const MATH_INLINE_RE    = /@@STX_MATH_INLINE_(\d+)@@/g;

/**
 * Repair common content-authoring mistakes BEFORE passing to KaTeX.
 *
 * Most of the textbook summaries on S3 were authored by humans + an
 * LLM helper and occasionally use TeX-document-style commands inside
 * inline math, where the equivalent math-mode command would actually
 * work. Without these fixes, KaTeX renders the entire expression in
 * red as an "all-or-nothing" error.
 *
 * Fixes:
 *   \textbf{...} -> \mathbf{...}  (text-mode-only command; in math
 *                                  mode, `^` and `_` inside text
 *                                  fail, but math-bold works fine)
 *   \textit{...} -> \mathit{...}
 *   \texttt{...} -> \mathtt{...}
 *   \textrm{...} -> \mathrm{...}
 */
function repairMathSource(src: string): string {
  // Recover double-escaped LaTeX. LLM-authored flashcards sometimes emit
  // `\\frac`, `\\theta`, `\\sin` (a backslash escaping the backslash) inside
  // the JSON string; KaTeX reads `\\` as a line break, so the command name
  // collapses to plain letters ("fracsqrt32"). Only collapse when EVERY
  // backslash in the expression is doubled -- i.e. stripping the `\\` pairs
  // leaves no lone `\` -- so normal single-escaped math and intentional `\\`
  // line breaks in matrices/cases (which sit beside single-backslash commands)
  // are never altered.
  const undoubled =
    src.includes("\\\\") && !src.replace(/\\\\/g, "").includes("\\")
      ? src.replace(/\\\\/g, "\\")
      : src;
  return undoubled
    .replace(/\\textbf\{/g, "\\mathbf{")
    .replace(/\\textit\{/g, "\\mathit{")
    .replace(/\\texttt\{/g, "\\mathtt{")
    .replace(/\\textrm\{/g, "\\mathrm{");
}

function renderMath(src: string, displayMode: boolean): string {
  const repaired = repairMathSource(src);
  // throwOnError: true so we can catch + render a clean fallback ourselves.
  // KaTeX's built-in fallback wraps the entire expression in a red
  // "katex-error" span -- visually shouty + indistinguishable from raw
  // LaTeX text -- not what we want in a learning UI.
  try {
    return katex.renderToString(repaired, {
      displayMode,
      throwOnError: true,
      strict: "ignore",
      output: "html",
    });
  } catch {
    // Quiet fallback: render the source as italic muted text. The author
    // should fix the TeX, but the student shouldn't see a red error.
    const wrapper = displayMode
      ? "block my-2 px-2 py-1 rounded bg-surface-2 text-text-muted text-sm italic font-mono overflow-x-auto"
      : "italic text-text-muted font-mono text-[0.95em]";
    return `<span class="${wrapper}">${escapeHtml(src)}</span>`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      case "'": return "&#39;";
      default:  return c;
    }
  });
}
